/**
 * Freshdesk Companies tab - the synced Freshdesk companies (`fd_companies`),
 * each matched to a franchise partner by name. Reads the cached list (no live FD
 * call) and renders it through the reusable `IntegrationSectionTab` shell: one
 * "Sync now" (the `freshdesk_companies` run) over a single Companies table.
 *
 * Matching is by name (there is no fp_code custom field on the FD company), so a
 * row is either Matched (a single active franchise_name match) or Unmatched
 * ([OLD] / internal / duplicate-name companies) - surfaced via the Match chip +
 * the Match filter so HQ can see the gaps.
 */
import React from 'react'
import { Box, Chip, MenuItem, TextField, Typography } from '@mui/material'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'

import { useDataFetching } from '../../../../../hooks/useDataFetching'
import {
  FreshdeskCompanies,
  FreshdeskCompany,
  integrationsApi,
} from '../../../../../services/admin/integrationsApi'
import { getMarketChipColor } from '../../../../../utils/marketChip'
import IntegrationSectionTab, {
  SectionEntityTable,
} from '../shared/IntegrationSectionTab'

const KEY = 'freshdesk_companies'

const marketChip = (market: string | null) =>
  market ? <Chip label={market} size="small" color={getMarketChipColor(market)} /> : '-'

const matchChip = (method: string) =>
  method === 'unmatched' ? (
    <Chip size="small" variant="outlined" color="default" label="Unmatched" />
  ) : (
    <Chip size="small" variant="outlined" color="success" label="Matched" />
  )

// Matched FP cell: fp_code (bold) + franchise name (muted). '-' when unmatched.
const fpCell = (company: FreshdeskCompany) => {
  if (!company.fp_code) return '-'
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
        {company.fp_code}
      </Typography>
      {company.franchise_name && (
        <Typography variant="body2" component="span" color="text.secondary">
          {company.franchise_name}
        </Typography>
      )}
    </Box>
  )
}

const FreshdeskCompaniesTab: React.FC = () => {
  const { data, loading, refetch } = useDataFetching<FreshdeskCompanies>({
    fetchFn: () => integrationsApi.getFreshdeskCompanies(),
    dependencies: [],
  })

  const companies = data?.companies ?? []

  const entities: SectionEntityTable[] = [
    {
      key: 'companies',
      label: 'Companies',
      icon: <BusinessOutlinedIcon fontSize="small" />,
      rows: companies,
      rowKey: (c: FreshdeskCompany) => String(c.id),
      defaultSortField: 'name',
      getMarket: (c: FreshdeskCompany) => c.market ?? '',
      searchText: (c: FreshdeskCompany) =>
        `${c.name} ${c.sub_code ?? ''} ${c.fp_code ?? ''} ${c.franchise_name ?? ''}`,
      searchPlaceholder: 'Search company, code or FP',
      extraFilter: {
        render: (params, setParams) => (
          <TextField
            select
            size="small"
            label="Match"
            value={params.get('match') ?? ''}
            onChange={(e) => {
              const next = new URLSearchParams(params)
              next.delete('match')
              if (e.target.value) next.set('match', e.target.value)
              setParams(next)
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="matched">Matched</MenuItem>
            <MenuItem value="unmatched">Unmatched</MenuItem>
          </TextField>
        ),
        predicate: (c: FreshdeskCompany, params) => {
          const match = params.get('match')
          if (match === 'matched') return c.match_method !== 'unmatched'
          if (match === 'unmatched') return c.match_method === 'unmatched'
          return true
        },
        isActive: (params) => !!params.get('match'),
        clear: (params) => {
          const next = new URLSearchParams(params)
          next.delete('match')
          return next
        },
      },
      columns: [
        { key: 'market', label: 'Market', sortable: true, render: (c: FreshdeskCompany) => marketChip(c.market) },
        { key: 'name', label: 'Company', sortable: true },
        { key: 'sub_code', label: 'Code', sortable: true, render: (c: FreshdeskCompany) => c.sub_code || '-' },
        { key: 'fp_code', label: 'Franchise partner', sortable: true, render: (c: FreshdeskCompany) => fpCell(c) },
        { key: 'match_method', label: 'Match', sortable: true, render: (c: FreshdeskCompany) => matchChip(c.match_method) },
      ],
    },
  ]

  return (
    <IntegrationSectionTab
      title="Companies"
      titleIcon={<BusinessOutlinedIcon />}
      runKey={KEY}
      syncLabel="Companies"
      loading={loading && !data}
      entities={entities}
      onSynced={refetch}
    />
  )
}

export default FreshdeskCompaniesTab
